import type { ReactNode } from 'react';

import { useMemo, useRef, useState } from 'react';

import {
  AutocompleteTextarea,
  type AutocompleteTextareaRenderProps,
} from '../AutocompleteTextarea/AutocompleteTextarea';
import {
  getAutocompleteTriggerRange,
  insertAutocompleteSuggestion,
  updateAutocompleteQuery,
} from '../AutocompleteTextarea/utils';

export type MentionSuggestion = {
  id: string;
  label: string;
};

export type MentionAutocompleteTextareaProps = {
  children: (props: AutocompleteTextareaRenderProps) => ReactNode;
  onChange: (value: string) => void;
  suggestions: readonly MentionSuggestion[];
};

function getTriggerRange(value: string, cursor: number) {
  return getAutocompleteTriggerRange(value, cursor, /(^|\s)@([^@\n]*)$/);
}

function getMentionKey(suggestion: MentionSuggestion) {
  return suggestion.id;
}

function renderMentionOption(suggestion: MentionSuggestion) {
  return <span className="min-w-0 wrap-break-word">{suggestion.label}</span>;
}

export function MentionAutocompleteTextarea({
  children,
  onChange,
  suggestions,
}: MentionAutocompleteTextareaProps) {
  const [query, setQuery] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLTextAreaElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const filteredSuggestions = useMemo(() => {
    if (query === null) return [];

    const normalizedQuery = query.trim().toLocaleLowerCase();
    return normalizedQuery
      ? suggestions.filter(({ label }) =>
          label.toLocaleLowerCase().includes(normalizedQuery),
        )
      : suggestions;
  }, [query, suggestions]);
  const isOpen = query !== null && filteredSuggestions.length > 0;

  function close() {
    setQuery(null);
    setAnchorEl(null);
  }

  function selectSuggestion(suggestion: MentionSuggestion) {
    const mention = `@${suggestion.label} `;
    return insertAutocompleteSuggestion({
      getTriggerRange,
      onChange,
      replacement: mention,
      textarea: textareaRef.current,
    });
  }

  return (
    <AutocompleteTextarea
      anchorEl={anchorEl}
      getOptionKey={getMentionKey}
      isOpen={isOpen}
      onChange={(event) => {
        onChange(event.target.value);
        updateAutocompleteQuery(event, getTriggerRange, setAnchorEl, setQuery);
      }}
      onClose={close}
      onSelect={selectSuggestion}
      options={filteredSuggestions}
      renderOption={renderMentionOption}
      textareaRef={textareaRef}
    >
      {children}
    </AutocompleteTextarea>
  );
}
